import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import {
  MessagePayload,
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage
} from 'firebase/messaging';
import { BehaviorSubject } from 'rxjs';
import { SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

type NotificationPermissionState = NotificationPermission | 'unsupported' | 'unconfigured';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  readonly foregroundMessage$ = new BehaviorSubject<MessagePayload | null>(null);
  readonly permission$ = new BehaviorSubject<NotificationPermissionState>('unsupported');
  readonly token$ = new BehaviorSubject<string | null>(null);

  private readonly supabaseClient: SupabaseClient;
  private messaging?: Messaging;
  private serviceWorkerRegistration?: ServiceWorkerRegistration;
  private foregroundUnsubscribe?: () => void;
  private initialized = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone
  ) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
    this.permission$.next(this.getInitialPermissionState());
  }

  async initializeForUser(userId: string): Promise<string | null> {
    if (!this.canRunInBrowser()) return null;
    if (!this.hasFirebaseConfig()) {
      this.permission$.next('unconfigured');
      return null;
    }

    const supported = await isSupported();
    if (!supported) {
      this.permission$.next('unsupported');
      return null;
    }

    await this.setupMessaging();
    this.listenForegroundMessages();

    if (Notification.permission !== 'granted') {
      this.permission$.next(Notification.permission);
      return null;
    }

    return this.refreshToken(userId);
  }

  async requestPermissionAndRegister(userId: string): Promise<string | null> {
    if (!this.canRunInBrowser()) return null;
    if (!this.hasFirebaseConfig()) {
      this.permission$.next('unconfigured');
      return null;
    }

    const supported = await isSupported();
    if (!supported) {
      this.permission$.next('unsupported');
      return null;
    }

    const permission = await Notification.requestPermission();
    this.permission$.next(permission);

    if (permission !== 'granted') {
      return null;
    }

    await this.setupMessaging();
    this.listenForegroundMessages();
    return this.refreshToken(userId);
  }

  async deleteCurrentToken(): Promise<void> {
    if (!this.messaging) return;

    const token = this.token$.value;
    await deleteToken(this.messaging);
    this.token$.next(null);

    if (token) {
      await this.supabaseClient.from('fcm_tokens').delete().eq('token', token);
    }
  }

  private async refreshToken(userId: string): Promise<string | null> {
    if (!this.messaging || !environment.firebase.vapidKey) return null;

    const token = await getToken(this.messaging, {
      vapidKey: environment.firebase.vapidKey,
      serviceWorkerRegistration: this.serviceWorkerRegistration
    });

    if (!token) return null;

    this.token$.next(token);
    await this.saveToken(userId, token);
    return token;
  }

  private async setupMessaging(): Promise<void> {
    if (this.initialized) return;

    const app = this.getFirebaseApp();
    this.serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope'
    });
    this.messaging = getMessaging(app);
    this.initialized = true;
  }

  private listenForegroundMessages(): void {
    if (!this.messaging || this.foregroundUnsubscribe) return;

    this.foregroundUnsubscribe = onMessage(this.messaging, (payload) => {
      this.zone.run(() => {
        this.foregroundMessage$.next(payload);
      });
    });
  }

  private async saveToken(userId: string, token: string): Promise<void> {
    const { error } = await this.supabaseClient
      .from('fcm_tokens')
      .upsert({
        user_id: userId,
        token,
        device_id: this.getDeviceId(),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        last_seen_at: new Date().toISOString()
      }, {
        onConflict: 'token'
      });

    if (error) {
      console.warn('FCM token 儲存失敗，請確認 Supabase 已建立 fcm_tokens table：', error.message);
    }
  }

  private getFirebaseApp(): FirebaseApp {
    if (getApps().length > 0) {
      return getApp();
    }

    return initializeApp(environment.firebase.config as FirebaseOptions);
  }

  private getDeviceId(): string {
    const storageKey = 'fcm-device-id';
    const existingId = localStorage.getItem(storageKey);

    if (existingId) {
      return existingId;
    }

    const newId = crypto.randomUUID();
    localStorage.setItem(storageKey, newId);
    return newId;
  }

  private canRunInBrowser(): boolean {
    return isPlatformBrowser(this.platformId) && 'serviceWorker' in navigator && 'Notification' in window;
  }

  private hasFirebaseConfig(): boolean {
    const config = environment.firebase?.config;
    return Boolean(
      environment.firebase?.vapidKey &&
      config?.apiKey &&
      config?.projectId &&
      config?.messagingSenderId &&
      config?.appId
    );
  }

  private getInitialPermissionState(): NotificationPermissionState {
    if (!isPlatformBrowser(this.platformId) || !('Notification' in globalThis)) {
      return 'unsupported';
    }

    if (!this.hasFirebaseConfig()) {
      return 'unconfigured';
    }

    return Notification.permission;
  }
}
